
import { SimulationConfig, ParallaxEffectType, Vector2D } from '@/types';
import { ParallaxRegistry } from './parallax/ParallaxRegistry';
import { initializeParallaxRegistry } from './parallax/RegistryInitializer';

export class ParallaxRenderer {
  private static initialized = false;

  private static init() {
    if (this.initialized) return;
    initializeParallaxRegistry();
    this.initialized = true;
  }

  public static draw(
    ctx: CanvasRenderingContext2D,
    config: SimulationConfig,
    cam: { x: number, y: number, scale: number },
    width: number,
    height: number,
    globalHue: number
  ) {
    if (!config.parallaxEnabled || config.parallaxEffect === 'NONE') return;
    this.init();

    const { 
        parallaxEffect, 
        parallaxIntensity, 
        parallaxLayers, 
        parallaxOpacity, 
        parallaxZoomInfluence,
        parallaxBlur,
        parallaxBrightness,
        parallaxContrast,
        parallaxSaturation,
        parallaxAutoScrollX,
        parallaxAutoScrollY,
        parallaxRotation
    } = config;
    
    const centerX = width / 2;
    const centerY = height / 2;
    const time = Date.now() * 0.001;

    // Camera position in world space
    const camWorldX = -cam.x;
    const camWorldY = -cam.y;

    ctx.save();
    
    // Apply global filters
    if (parallaxBlur > 0 || parallaxBrightness !== 1 || parallaxContrast !== 1 || parallaxSaturation !== 1) {
        ctx.filter = `blur(${parallaxBlur}px) brightness(${parallaxBrightness}) contrast(${parallaxContrast}) saturate(${parallaxSaturation})`;
    }

    const effectiveZoom = 1 + (cam.scale - 1) * parallaxZoomInfluence;

    for (let i = 1; i <= parallaxLayers; i++) {
      const layerFactor = i / parallaxLayers;
      const depth = layerFactor * parallaxIntensity;
      
      // Calculate offset for this layer including auto-scroll
      const autoX = (parallaxAutoScrollX || 0) * time * layerFactor * 100;
      const autoY = (parallaxAutoScrollY || 0) * time * layerFactor * 100;
      const offsetX = -camWorldX * depth + autoX;
      const offsetY = -camWorldY * depth + autoY;

      ctx.save();
      ctx.globalAlpha = parallaxOpacity * (1 - layerFactor * 0.4);
      
      // Apply parallax offset
      ctx.translate(centerX + offsetX, centerY + offsetY);
      ctx.scale(effectiveZoom, effectiveZoom);
      if (parallaxRotation !== 0) {
          ctx.rotate(parallaxRotation * Math.PI / 180);
      }

      const color = this.getLayerColor(config, i, globalHue);
      
      ParallaxRegistry.render(parallaxEffect, {
          ctx,
          config,
          layerIndex: i,
          globalHue,
          width,
          height,
          camX: camWorldX,
          camY: camWorldY,
          time,
          color
      });
      
      ctx.restore();
    }

    ctx.restore();
  }

  private static getLayerColor(config: SimulationConfig, layerIndex: number, globalHue: number): string {
    if (config.parallaxColorMode === 'RAINBOW') {
      return `hsl(${(globalHue + layerIndex * 30) % 360}, 70%, 60%)`;
    }
    if (config.parallaxColorMode === 'CUSTOM') {
      return config.parallaxCustomColor;
    }
    // THEME mode or fallback
    if (config.darkMode) {
      return `rgba(255, 255, 255, ${0.2 + (1 - layerIndex / config.parallaxLayers) * 0.5})`;
    }
    return `rgba(0, 0, 0, ${0.1 + (1 - layerIndex / config.parallaxLayers) * 0.3})`;
  }
}
